import { useEffect, useRef, useMemo, useState } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage } from 'react-intl';
import { AGGClient } from 'src/clients/agg-client/agg-client';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { Tracking } from '@/analytics/atl-analytics/tracking';
import { useAnalyticsClient } from '@/analytics/wac-analytics/analytics-client';
import { Button } from '@/libraries/design/components/button/src/button';
import { LOCALES } from '@/libraries/web-framework/src/localization/localization.constants';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { ProductKeyTypes } from '@/libraries/wpl/foundations/src/data-event-props';
import { dynamicConfigManifest } from '@/utils/dynamic-configs/dynamic-config-manifest';
import { getDynamicConfig } from '@/utils/dynamic-configs/get-dynamic-config/get-dynamic-config';
import { useLocation } from '@/utils/location-provider';
import { useLoggedInUser } from '@/utils/logged-in-user-provider';

import { getIsSMBSalesLead } from './contact-sales-cta-helpers';

const fullWidthStyles = css({
	maxWidth: 'none',
	width: '100%',
});

const SALES_LEADS_ROUTES = ['/', '/software/confluence', '/software/jira'];

const toNonLocalizedRoute = (pathname: string): string => {
	const locale = LOCALES.find(
		(candidate) => pathname === `/${candidate}` || pathname.startsWith(`/${candidate}/`),
	);
	const withoutLocale = locale ? pathname.slice(locale.length + 1) : pathname;
	return withoutLocale.replace(/\/+$/, '') || '/';
};

export const isSalesLeadsCtaRoute = (routes: string[], pathname: string): boolean =>
	routes.some((route) => toNonLocalizedRoute(route) === toNonLocalizedRoute(pathname));

export const useContactSalesCtaEligibility = (): {
	isEligible: boolean;
	isLoading: boolean;
} => {
	const { accountId } = useLoggedInUser();
	const { pathname } = useLocation();
	// the CTA is only rendered on the page paths listed in this dynamic config
	// https://switcheroo.atlassian.com/ui/dynamic-configs/key/wac-sales-leads-cta-route-list
	const routes = getDynamicConfig({
		config: dynamicConfigManifest.routesAllowingSalesLeadsCta,
		defaultValue: SALES_LEADS_ROUTES,
	});
	const isRouteAllowed = isSalesLeadsCtaRoute(routes, pathname);

	const [isSalesLead, setIsSalesLead] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	useEffect(() => {
		if (!isRouteAllowed || !accountId) return;
		setIsLoading(true);
		const aggClient = new AGGClient({
			gatewayUrl:
				process.env['NODE_ENV'] === 'production'
					? 'https://www.atlassian.com/gateway/api/graphql'
					: 'https://wac.stg.internal.atlassian.com/gateway/api/graphql',
		});

		getIsSMBSalesLead({
			accountId,
			aggClient,
		})
			.then((response) => {
				setIsSalesLead(!!response);
			})
			.catch(() => {
				console.error('Error fetching GUPS user profile for SMB Sales lead');
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [accountId, isRouteAllowed]);

	if (!accountId || !isRouteAllowed) {
		return { isEligible: false, isLoading: false };
	}

	return {
		isEligible: isSalesLead,
		isLoading,
	};
};

export const ContactSalesCta = ({ isFullWidth }: { isFullWidth?: boolean }) => {
	const { isEligible, isLoading } = useContactSalesCtaEligibility();
	const productKey = useMemo<ProductKeyTypes>(() => {
		const pathname = globalThis.location?.pathname ?? '';
		if (pathname.includes('/software/jira')) return 'jira';
		if (pathname.includes('/software/confluence')) return 'confluence';
		if (pathname.includes('/enterprise/contact')) return 'enterpriseContactSales';
		if (pathname === '/') return 'home';
		return undefined;
	}, []);

	if (isLoading || !isEligible) {
		return;
	}

	return <CtaButtonRenderer isFullWidth={isFullWidth} productKey={productKey} />;
};

const CtaButtonRenderer = ({
	isFullWidth,
	productKey,
}: {
	isFullWidth?: boolean;
	productKey: ProductKeyTypes;
}) => {
	const analyticsClient = useAnalyticsClient();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const localizedHref = useLocalizedHref('/enterprise/contact?formType=product-features');

	const hasFiredRef = useRef(false);

	useEffect(() => {
		if (analyticsClient && !hasFiredRef.current) {
			hasFiredRef.current = true;
			createAnalyticsEvent({
				eventType: 'ui',
				action: 'viewed',
				actionSubject: 'button',
				actionSubjectId: 'contactSalesCta',
				source: 'globalNav',
				attributes: {
					isMobile: !!isFullWidth,
					productKey: productKey,
				},
			}).fire();
		}
	}, [analyticsClient, createAnalyticsEvent, isFullWidth]);

	useEffect(() => {
		Tracking.autoTrackLinks(true);
	}, []);

	return (
		<Button
			variant="primary_alt"
			css={isFullWidth && fullWidthStyles}
			href={localizedHref}
			dataEventProps={{
				'data-product-key': productKey,
				'data-event': 'clicked',
				'data-event-component': 'linkButton',
				'data-event-container': 'navbar',
				'data-label-english': 'Contact us',
			}}
		>
			<FormattedMessage id="global-nav.contact-sales-cta.label" defaultMessage="Contact us" />
		</Button>
	);
};
